import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import {
  Check,
  ChevronDown,
  Clock,
  LoaderCircle,
  MoreVertical,
  Search,
  Square,
} from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { stop_alert_run } from "../../watchlist/api";
import { splitApiRequestFormErrors } from "../../api";
import { compareAlerts } from "../../watchlist/alertFilters";
import { useRunProgress } from "../../watchlist/hooks";
import type { Alert } from "../../watchlist/types";
import type { AlertHandlers } from "./alertActions";
import { useAlertActions } from "./alertActions";
import { useAlertLabels } from "./labels";

type AlertListProps = {
  /** The alerts inside the page's people and process filters. */
  alerts: Alert[];
  /** Every saved alert, filtered or not: tells "none yet" from "none match". */
  totalCount: number;
  filter: AlertFilter;
  query: string;
  onFilterChange: (filter: AlertFilter) => void;
  onQueryChange: (query: string) => void;
  onOpen: (alert: Alert) => void;
  /** Bound per row, so the list itself stays free of the alert being acted on. */
  handlersFor: (alert: Alert) => AlertHandlers;
};

export type AlertFilter = "all" | "active" | "paused";

const statusOf = (alert: Alert): Exclude<AlertFilter, "all"> => {
  return alert.enabled ? "active" : "paused";
};

/** Pill with a leading dot: the row's state, and the unread marker beside it. */
const Badge = ({
  tone,
  dot,
  className,
  children,
}: {
  tone: string;
  dot: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <span
    className={clsx(
      "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
      tone,
      className,
    )}
  >
    <span className={clsx("h-2 w-2 rounded-full", dot)} />
    {children}
  </span>
);

const AlertRow = ({
  alert,
  onOpen,
  handlers,
}: { alert: Alert; onOpen: () => void; handlers: AlertHandlers }) => {
  const { t } = useTranslation();
  const { relativeTime, cadence } = useAlertLabels();
  const actions = useAlertActions(alert, handlers);
  const progress = useRunProgress(alert.id);
  const isRunning = progress?.status === "running";
  const isPaused = !alert.enabled;
  const lastRun = alert.runs[0];
  const shownTitle = alert.title || alert.prompt;
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const skipRenameCommit = useRef(false);
  const timing = [cadence(alert.schedule), lastRun && relativeTime(lastRun.ranAt)]
    .filter(Boolean)
    .join(" · ");

  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const commitRename = async (value: string) => {
    const title = value.trim();
    if (title === "" || title === shownTitle.trim()) {
      setEditingTitle(null);
      return;
    }
    setRenaming(true);
    setRenameError(null);
    try {
      await handlers.onRename(title);
      setEditingTitle(null);
    } catch (error) {
      const parsed = splitApiRequestFormErrors<{ title: string }>(error, t, ["title"]);
      setRenameError(parsed.fieldErrors.title ?? parsed.remainder);
    } finally {
      setRenaming(false);
    }
  };

  return (
    <li
      aria-busy={isRunning}
      className={clsx(
        // Flat cards: a hairline and the surface itself are enough to bound a
        // row this large, and a stack of drop shadows down the page made the
        // list read as a pile of floating objects rather than one column.
        "relative rounded-2xl border px-5 py-4 shadow-none transition",
        isPaused ? "border-outline/60 bg-surface/55" : "bg-surface hover:border-primary/40",
        !isPaused && (alert.unread ? "border-unread/50 bg-unread/[0.035]" : "border-outline/70"),
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={event => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen();
          }
        }}
        className={clsx(
          "block w-full min-w-0 cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          "pr-8",
        )}
      >
        <span className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-2">
          {/* The alert's name is the row's anchor, falling back to its question
              for rows saved before generated titles existed. */}
          <span
            className={clsx(
              "min-w-0 flex-1 text-sm font-semibold text-text transition-opacity",
              isPaused && "opacity-55",
            )}
          >
            {editingTitle !== null ? (
              <input
                autoFocus
                value={editingTitle}
                disabled={renaming}
                onClick={stopRowNavigation}
                onFocus={event => event.currentTarget.select()}
                onChange={event => setEditingTitle(event.target.value)}
                onBlur={event => {
                  if (skipRenameCommit.current) {
                    skipRenameCommit.current = false;
                    return;
                  }
                  void commitRename(event.currentTarget.value);
                }}
                onKeyDown={event => {
                  event.stopPropagation();
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    skipRenameCommit.current = true;
                    setEditingTitle(null);
                    setRenameError(null);
                  }
                }}
                className="h-6 w-full rounded border border-outline bg-surface px-1.5 text-sm font-semibold text-text outline-none focus:border-info focus:ring-1 focus:ring-info/30"
                aria-label={t("watchlist.rename_alert")}
              />
            ) : (
              <button
                type="button"
                disabled={renaming}
                onClick={event => {
                  stopRowNavigation(event);
                  setEditingTitle(shownTitle);
                  setRenameError(null);
                }}
                className="line-clamp-2 cursor-text rounded text-left underline-offset-2 decoration-dotted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30 disabled:opacity-50"
                title={t("watchlist.rename_alert")}
              >
                {shownTitle}
              </button>
            )}
            {renameError && (
              <span className="mt-1 block truncate text-xs font-normal text-danger-strong">
                {renameError}
              </span>
            )}
            {lastRun?.status === "success" && lastRun.headline && (
              <span className="mt-1 block line-clamp-2 text-xs font-normal text-text-dim">
                {lastRun.headline}
              </span>
            )}
          </span>

          {isRunning ? (
            <span className="flex shrink-0 items-center gap-1.5">
              <span className="flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success-strong">
                <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
                {t("watchlist.run_in_progress")}
              </span>
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  stop_alert_run(alert.id);
                }}
                className="rounded-md bg-muted-surface/60 p-1.5 text-text-dim/70 transition hover:bg-muted-surface hover:text-text-mid focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label={t("chat_stop")}
                title={t("chat_stop")}
              >
                <Square size={12} aria-hidden="true" />
              </button>
            </span>
          ) : progress?.status === "failed" ? (
            <Badge tone="bg-danger/10 font-semibold text-danger" dot="bg-danger">
              {t("watchlist.run_failed")}
            </Badge>
          ) : alert.unread ? (
            <Badge
              tone="bg-unread/10 font-semibold text-unread"
              dot="unread-pulse bg-unread text-unread"
            >
              {t("watchlist.unread")}
            </Badge>
          ) : (
            <Badge
              tone={
                alert.enabled
                  ? "bg-info/10 font-semibold text-info"
                  : "bg-muted-surface text-text-mid"
              }
              dot={alert.enabled ? "bg-info" : "bg-muted"}
            >
              {alert.enabled ? t("watchlist.running") : t("paused")}
            </Badge>
          )}
        </span>

        {timing && (
          <span
            className={clsx(
              "mt-2 flex items-center gap-1 text-xs text-text-dim transition-opacity",
              isPaused && "opacity-55",
            )}
          >
            <Clock size={12} />
            {timing}
          </span>
        )}
      </div>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className="absolute right-2 top-3.5 rounded-lg p-1.5 text-text-dim outline-none transition hover:bg-muted-surface hover:text-text focus-visible:ring-2 focus-visible:ring-primary/40"
          title={t("watchlist.alert_actions")}
        >
          <MoreVertical size={16} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={4}
            className="z-50 w-44 rounded-xl border border-outline bg-surface p-1 shadow-xl"
          >
            {actions.map(({ key, Icon, label, run, danger }) => (
              <DropdownMenu.Item
                key={key}
                onSelect={run}
                className={clsx(
                  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-none transition data-[highlighted]:bg-muted-surface",
                  danger ? "text-danger" : "text-text",
                )}
              >
                <Icon size={16} />
                {label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </li>
  );
};

/** One row per saved alert — never one per firing, so a noisy alert stays one badge. */
const AlertList = ({
  alerts,
  totalCount,
  onOpen,
  handlersFor,
  filter,
  query,
  onFilterChange: setFilter,
  onQueryChange: setQuery,
}: AlertListProps) => {
  const { t } = useTranslation();

  const counts = useMemo(
    () => ({
      all: alerts.length,
      active: alerts.filter(alert => statusOf(alert) === "active").length,
      paused: alerts.filter(alert => statusOf(alert) === "paused").length,
    }),
    [alerts],
  );

  const options: { value: AlertFilter; label: string }[] = [
    { value: "all", label: t("watchlist.filter_all") },
    { value: "active", label: t("watchlist.running") },
    { value: "paused", label: t("paused") },
  ];
  const selected = options.find(option => option.value === filter) ?? options[0];
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleAlerts = alerts
    .filter(alert => {
      if (filter !== "all" && statusOf(alert) !== filter) return false;
      if (!normalizedQuery) return true;
      const latest = alert.runs[0];
      const latestHeadline =
        latest?.status === "success"
          ? latest.headline
          : latest?.status === "failed"
            ? t("watchlist.run_failed")
            : "";
      return [alert.title, alert.prompt, latestHeadline].some(value =>
        value.toLocaleLowerCase().includes(normalizedQuery),
      );
    })
    .sort(compareAlerts);

  return (
    <section aria-labelledby="alerts-kpis-heading">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 id="alerts-kpis-heading" className="mr-1 text-base font-semibold text-text">
          {t("watchlist.kpis_tracking")}
        </h2>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex h-7 min-w-24 items-center gap-1 rounded-lg border border-outline bg-surface px-2 text-xs text-text-mid outline-none transition hover:border-primary/40 hover:text-text focus-visible:ring-2 focus-visible:ring-primary/40">
            {selected.label}
            <span className="ml-auto rounded-md bg-muted-surface px-1.5 py-0.5 text-[10px] text-text-mid">
              {counts[filter]}
            </span>
            <ChevronDown size={12} className="text-text-dim" aria-hidden="true" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              className="z-50 w-40 rounded-xl border border-outline bg-surface p-1 shadow-xl"
            >
              {options.map(option => (
                <DropdownMenu.Item
                  key={option.value}
                  onSelect={() => setFilter(option.value)}
                  className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-text outline-none data-[highlighted]:bg-muted-surface"
                >
                  <span className="flex-1">{option.label}</span>
                  <span className="rounded-md bg-muted-surface px-1.5 py-0.5 text-[11px] text-text-mid">
                    {counts[option.value]}
                  </span>
                  <Check
                    size={15}
                    className={clsx("text-primary", option.value !== filter && "invisible")}
                    aria-hidden="true"
                  />
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <label className="relative ml-auto block w-full sm:w-60">
          <span className="sr-only">{t("watchlist.search_alerts")}</span>
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-dim"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t("watchlist.search_alerts")}
            className="h-8 w-full rounded-lg border border-outline bg-surface pl-8 pr-2.5 text-xs text-text outline-none transition placeholder:text-text-dim hover:border-primary/40 focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
          />
        </label>
      </div>

      {visibleAlerts.length > 0 ? (
        <ul className="flex flex-col gap-2.5">
          {visibleAlerts.map(alert => (
            <AlertRow
              key={alert.id}
              alert={alert}
              onOpen={() => onOpen(alert)}
              handlers={handlersFor(alert)}
            />
          ))}
        </ul>
      ) : (
        <p className="rounded-xl border border-dashed border-outline px-4 py-8 text-center text-xs leading-relaxed text-text-dim">
          {totalCount === 0 ? t("watchlist.no_alerts") : t("watchlist.no_matching_alerts")}
        </p>
      )}
    </section>
  );
};

export default AlertList;
